import { z } from 'zod';
import { BedrockUrlSchema, BedrockSettingsSchema } from './bedrock.js';
import { ModEntrySchema, ModChangeSchema } from './mods.js';
import { NetworkStatusSchema } from './network-status.js';

export const VelocityPluginApplySchema = ModChangeSchema.extend({
  previewHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
});
export const VELOCITY_CAPABILITY = 'velocity-manage-v1';
export const VELOCITY_MANAGE_CAPABILITY = 'velocity-manage-2';
export const FloodgateKeySchema = z.string().min(16).max(16384);
export const VelocityPluginFileSchema = z
  .object({
    name: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9._+-]*\.jar$/),
    url: BedrockUrlSchema,
    sha512: z
      .string()
      .regex(/^[0-9a-f]{128}$/)
      .optional(),
    sha256: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
    size: z
      .number()
      .int()
      .positive()
      .max(200 * 1024 * 1024),
  })
  .strict()
  .refine((file) => Boolean(file.sha512 || file.sha256), 'Checksum required');
export type VelocityPluginFile = z.infer<typeof VelocityPluginFileSchema>;
export const VelocityStatusSchema = z.object({
  state: z.enum(['running', 'stopped', 'unknown']),
  version: z.string().max(200).optional(),
  plugins: z.array(z.string().max(255)).max(500),
  lastRestart: z.string().max(100).optional(),
});
export const VelocityViewSchema = VelocityStatusSchema.extend({
  supported: z.boolean(),
  manageSupported: z.boolean().default(false),
  message: z.string().optional(),
  entries: z.array(ModEntrySchema),
  bedrock: BedrockSettingsSchema,
  keyReady: z.boolean(),
  network: NetworkStatusSchema.nullable(),
  edge: z.object({ state: z.string(), appliedAt: z.string().optional(), message: z.string().optional() }),
});
export type VelocityView = z.infer<typeof VelocityViewSchema>;

export const VelocityPathSchema = z
  .string()
  .max(500)
  .refine(
    (value) =>
      !value.startsWith('/') &&
      !value.includes('\\') &&
      !value.includes('\0') &&
      value.split('/').every((part) => part !== '..' && !part.includes(':')),
    'パスが正しくありません',
  );
export const VelocityFileRequestSchema = z.object({ path: VelocityPathSchema.default('') }).strict();
export const VelocityWriteSchema = z
  .object({
    path: VelocityPathSchema,
    content: z.string().max(1024 * 1024),
    expectedSha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
  })
  .strict();
export const VelocityProjectSchema = z.object({ versions: z.record(z.string(), z.array(z.string())) });
export const VelocityVersionsQuerySchema = z.object({ version: z.string().min(1).max(100).optional() }).strict();
export const VelocityVersionRequestSchema = z
  .object({
    version: z.string().min(1).max(100),
    build: z.number().int().positive(),
  })
  .strict();
export const VelocityDownloadUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    if (!URL.canParse(value)) return false;
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      ['fill.papermc.io', 'fill-data.papermc.io'].includes(url.hostname)
    );
  }, 'PaperMC 公式の配布 URL ではありません');
export const VelocityUpdateSchema = z
  .object({
    url: VelocityDownloadUrlSchema,
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    size: z
      .number()
      .int()
      .positive()
      .max(200 * 1024 * 1024),
  })
  .strict();
export const VelocityBuildSchema = z.object({
  id: z.number().int().positive(),
  channel: z.string(),
  downloads: z.record(
    z.string(),
    z.object({
      url: VelocityDownloadUrlSchema,
      size: VelocityUpdateSchema.shape.size,
      checksums: z.object({ sha256: VelocityUpdateSchema.shape.sha256 }),
    }),
  ),
});
export type VelocityBuild = z.infer<typeof VelocityBuildSchema>;
export interface VelocityVersions {
  version: string;
  versions: string[];
  latest: string;
  builds: VelocityBuild[];
}
